/**
 * Family calendar: the owner's Google calendar(s) ("Nat & Lauren"), synced by the server.
 *
 * The server (server/src/modules/calendar) holds the Google OAuth refresh token (encrypted),
 * syncs the selected calendars into SQLite (syncTokens + push channels / polling) with
 * recurring events expanded (singleEvents), and serves these compact shapes. Writes go to
 * Google first (etag-checked), then into the cache. Nothing Google-authenticated reaches
 * the browser.
 *
 * Times: timed events carry ISO strings with an offset plus precomputed epoch ms; all-day
 * events carry date keys (YYYY-MM-DD, end exclusive like Google) and ms at local midnight
 * in the calendar time zone (Europe/London). Pure time-zone and recurrence helpers live
 * here so server, web, mock and tests agree.
 */
import { z } from 'zod';
export const CALENDAR_TZ = 'Europe/London';
// ---------------------------------------------------------------- inputs
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const isIso = (s) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && Number.isFinite(Date.parse(s));
export const ReminderInput = z.object({ method: z.enum(['popup', 'email']), minutes: z.number().int().min(0).max(40320) });
const EventFields = {
    calendarId: z.string().min(1).max(256),
    title: z.string().trim().min(1).max(300),
    allDay: z.boolean(),
    start: z.string().max(40),
    end: z.string().max(40),
    timeZone: z.string().max(64).optional(),
    location: z.string().max(500).nullable().optional(),
    description: z.string().max(8000).nullable().optional(),
    /** RRULE / EXDATE lines (series only); null clears. */
    recurrence: z.array(z.string().max(500)).max(5).nullable().optional(),
    reminders: z.object({ useDefault: z.boolean(), overrides: z.array(ReminderInput).max(5) }).optional(),
    colorId: z.string().regex(/^\d{1,2}$/).nullable().optional(),
    attendees: z.array(z.object({ email: z.string().email().max(254), optional: z.boolean().optional() })).max(50).optional(),
};
function timesValid(v) {
    if (v.start === undefined && v.end === undefined)
        return true;
    if (v.start === undefined || v.end === undefined || v.allDay === undefined)
        return false;
    if (v.allDay)
        return DATE_KEY.test(v.start) && DATE_KEY.test(v.end) && v.end > v.start;
    return isIso(v.start) && isIso(v.end) && Date.parse(v.end) >= Date.parse(v.start);
}
export const EventInput = z.object(EventFields).refine(timesValid, { message: 'Check the start and end times', path: ['end'] });
/** PATCH: any subset; start/end/allDay travel together. */
export const EventPatchInput = z.object({
    patch: z.object(EventFields).partial().refine(timesValid, { message: 'Check the start and end times', path: ['end'] }),
    scope: z.enum(['instance', 'series']).default('instance'),
    /** The etag the client edited; a mismatch answers 409 with the current event. */
    etag: z.string().max(200).optional(),
});
export const CalendarSelectionInput = z.object({
    calendars: z.array(z.object({ id: z.string().min(1).max(256), selected: z.boolean(), color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional() })).max(100),
    defaultCalendarId: z.string().min(1).max(256).nullable().optional(),
});
// ---------------------------------------------------------------- colours
/** Google Calendar's fixed event palette (colors.get → event), by colorId. */
export const GOOGLE_EVENT_COLORS = {
    '1': { name: 'Lavender', hex: '#7986cb' },
    '2': { name: 'Sage', hex: '#33b679' },
    '3': { name: 'Grape', hex: '#8e24aa' },
    '4': { name: 'Flamingo', hex: '#e67c73' },
    '5': { name: 'Banana', hex: '#f6bf26' },
    '6': { name: 'Tangerine', hex: '#f4511e' },
    '7': { name: 'Peacock', hex: '#039be5' },
    '8': { name: 'Graphite', hex: '#616161' },
    '9': { name: 'Blueberry', hex: '#3f51b5' },
    '10': { name: 'Basil', hex: '#0b8043' },
    '11': { name: 'Tomato', hex: '#d50000' },
};
export const DEFAULT_CALENDAR_COLOR = '#c9925a';
/** The calendar new events default to: one named "Nat & Lauren" (any spacing / "and"), else the primary. */
export function preferredCalendarId(cals) {
    const norm = (s) => s.toLowerCase().replace(/\s+/g, ' ').replace(/\band\b/g, '&').trim();
    const named = cals.find((c) => c.writable && norm(c.name) === 'nat & lauren');
    return named?.id ?? cals.find((c) => c.writable && c.primary)?.id ?? cals.find((c) => c.writable)?.id ?? null;
}
// ---------------------------------------------------------------- time zones
const dtf = new Map();
function fmt(tz) {
    let f = dtf.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-GB', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
        });
        dtf.set(tz, f);
    }
    return f;
}
export function zonedParts(ms, tz) {
    const p = {};
    for (const x of fmt(tz).formatToParts(new Date(ms)))
        if (x.type !== 'literal')
            p[x.type] = Number(x.value);
    return { y: p['year'], m: p['month'], d: p['day'], h: p['hour'] % 24, mi: p['minute'], s: p['second'] };
}
/** Minutes the zone is ahead of UTC at this instant (London: 0 or 60). */
export function tzOffsetMin(ms, tz) {
    const p = zonedParts(ms, tz);
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
    return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}
const pad = (n, w = 2) => String(n).padStart(w, '0');
/** YYYY-MM-DD of an instant in the zone. */
export function dateKeyOf(ms, tz) {
    const p = zonedParts(ms, tz);
    return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}
/** Minutes since local midnight (0..1439). */
export function minutesOfDay(ms, tz) {
    const p = zonedParts(ms, tz);
    return p.h * 60 + p.mi;
}
export function parseDateKey(key) {
    const [y, m, d] = key.split('-').map(Number);
    return { y, m, d };
}
/** Epoch ms of a local wall-clock time (date key + minutes since midnight) in the zone. DST gaps resolve forwards. */
export function zonedToMs(key, minutes, tz) {
    const { y, m, d } = parseDateKey(key);
    const guess = Date.UTC(y, m - 1, d, 0, minutes);
    const off1 = tzOffsetMin(guess, tz);
    let t = guess - off1 * 60000;
    const off2 = tzOffsetMin(t, tz);
    if (off2 !== off1) {
        const t2 = guess - off2 * 60000;
        // In a spring-forward gap neither offset round-trips; take the later instant.
        t = tzOffsetMin(t2, tz) === off2 ? t2 : Math.max(t, t2);
    }
    return t;
}
/** Local midnight of the date key. */
export const dayStartMs = (key, tz) => zonedToMs(key, 0, tz);
export function addDaysKey(key, n) {
    const { y, m, d } = parseDateKey(key);
    const t = new Date(Date.UTC(y, m - 1, d + n));
    return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}
export function addMonthsKey(key, n) {
    const { y, m, d } = parseDateKey(key);
    const first = new Date(Date.UTC(y, m - 1 + n, 1));
    const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
    return `${first.getUTCFullYear()}-${pad(first.getUTCMonth() + 1)}-${pad(Math.min(d, last))}`;
}
/** Whole days from a to b (b - a). */
export function daysBetweenKeys(a, b) {
    const pa = parseDateKey(a);
    const pb = parseDateKey(b);
    return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 86_400_000);
}
/** 0 = Monday … 6 = Sunday. */
export function weekdayOf(key) {
    const { y, m, d } = parseDateKey(key);
    return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}
/** Monday of the key's week. */
export const weekStart = (key) => addDaysKey(key, -weekdayOf(key));
/** ISO string with the zone's offset, e.g. 2026-10-09T19:00:00+01:00. */
export function isoInZone(ms, tz) {
    const p = zonedParts(ms, tz);
    const off = tzOffsetMin(ms, tz);
    const sign = off < 0 ? '-' : '+';
    const a = Math.abs(off);
    return `${p.y}-${pad(p.m)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}${sign}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
}
/** Epoch ms bounds of an event's start / end (all-day: local midnights in tz). */
export function eventBounds(e, tz) {
    if (e.allDay)
        return { startMs: dayStartMs(e.start, tz), endMs: dayStartMs(e.end, tz) };
    return { startMs: Date.parse(e.start), endMs: Date.parse(e.end) };
}
/** The date keys an event touches (all-day end is exclusive; a timed event ending at midnight doesn't touch the next day). */
export function eventDays(e, tz) {
    const first = e.allDay ? e.start : dateKeyOf(e.startMs, tz);
    const last = e.allDay ? addDaysKey(e.end, -1) : dateKeyOf(Math.max(e.startMs, e.endMs - 1), tz);
    const out = [];
    for (let k = first, i = 0; k <= last && i < 400; k = addDaysKey(k, 1), i++)
        out.push(k);
    return out.length ? out : [first];
}
export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
/** RRULE line for Google (`RRULE:FREQ=WEEKLY;BYDAY=MO,WE`). `until` is a date: for timed events Google wants UTC, so it's end of that day in tz. */
export function toRRule(r, allDay = true, tz = CALENDAR_TZ) {
    const parts = [`FREQ=${r.freq.toUpperCase()}`];
    if (r.interval > 1)
        parts.push(`INTERVAL=${r.interval}`);
    if (r.freq === 'weekly' && r.byDay?.length)
        parts.push(`BYDAY=${r.byDay.join(',')}`);
    if (r.until) {
        if (allDay)
            parts.push(`UNTIL=${r.until.replace(/-/g, '')}`);
        else {
            const t = new Date(dayStartMs(addDaysKey(r.until, 1), tz) - 1000);
            parts.push(`UNTIL=${t.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`);
        }
    }
    else if (r.count)
        parts.push(`COUNT=${r.count}`);
    return `RRULE:${parts.join(';')}`;
}
/** Parses the first RRULE line we can represent; null for anything fancier (BYSETPOS, BYMONTHDAY lists…). */
export function parseRRule(lines, tz = CALENDAR_TZ) {
    const line = lines?.find((l) => l.startsWith('RRULE:'));
    if (!line)
        return null;
    const kv = new Map(line.slice(6).split(';').map((p) => p.split('=')));
    const freq = kv.get('FREQ')?.toLowerCase();
    if (freq !== 'daily' && freq !== 'weekly' && freq !== 'monthly' && freq !== 'yearly')
        return null;
    const supported = new Set(['FREQ', 'INTERVAL', 'BYDAY', 'UNTIL', 'COUNT', 'WKST']);
    if ([...kv.keys()].some((k) => !supported.has(k)))
        return null;
    const rule = { freq, interval: Math.max(1, Number(kv.get('INTERVAL') ?? 1) || 1) };
    const by = kv.get('BYDAY');
    if (by) {
        if (freq !== 'weekly')
            return null;
        const days = by.split(',');
        if (!days.every((d) => WEEKDAY_CODES.includes(d)))
            return null;
        rule.byDay = WEEKDAY_CODES.filter((c) => days.includes(c));
    }
    const until = kv.get('UNTIL');
    if (until) {
        const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(until);
        if (!m)
            return null;
        if (m[4]) {
            const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
            rule.until = dateKeyOf(ms, tz);
        }
        else
            rule.until = `${m[1]}-${m[2]}-${m[3]}`;
    }
    const count = kv.get('COUNT');
    if (count)
        rule.count = Number(count) || null;
    return rule;
}
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};
/** "Every week on Monday and Wednesday", "Every 2 weeks", "Every year on 9 October", "Every weekday". */
export function describeRepeat(r, startKey) {
    const { m, d } = parseDateKey(startKey);
    const every = (unit) => (r.interval > 1 ? `Every ${r.interval} ${unit}s` : `Every ${unit}`);
    let base;
    switch (r.freq) {
        case 'daily':
            base = r.interval > 1 ? `Every ${r.interval} days` : 'Every day';
            break;
        case 'weekly': {
            const days = r.byDay?.length ? r.byDay : [WEEKDAY_CODES[weekdayOf(startKey)]];
            const idx = days.map((c) => WEEKDAY_CODES.indexOf(c)).sort((a, b) => a - b);
            if (r.interval === 1 && idx.join() === '0,1,2,3,4') {
                base = 'Every weekday';
                break;
            }
            if (r.interval === 1 && idx.length === 7) {
                base = 'Every day';
                break;
            }
            const names = idx.map((i) => DAY_NAMES[i]);
            const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : names[0];
            base = `${every('week')} on ${list}`;
            break;
        }
        case 'monthly':
            base = `${every('month')} on the ${ordinal(d)}`;
            break;
        case 'yearly':
            base = `${every('year')} on ${d} ${MONTHS[m - 1]}`;
            break;
    }
    if (r.until) {
        const u = parseDateKey(r.until);
        base += `, until ${u.d} ${MONTHS[u.m - 1].slice(0, 3)} ${u.y}`;
    }
    else if (r.count)
        base += `, ${r.count} times`;
    return base;
}
// ---------------------------------------------------------------- birthdays (app-managed, not Google)
/**
 * Birthdays are kept by the app itself (server modules/birthdays.ts), independent of Google: a name, a day
 * and month, an optional year. They show as all-day entries on a virtual "Birthdays" calendar.
 */
export const BIRTHDAY_CALENDAR_ID = 'birthdays';
export const BIRTHDAY_COLOR = '#d6699a';
const DAYS_IN = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const BirthdayInput = z.object({
    name: z.string().trim().min(1).max(80),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
    year: z.number().int().min(1900).max(2100).nullable().optional(),
    note: z.string().trim().max(200).nullable().optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
    /** Send-a-card to-do; default on. */
    card: z.boolean().optional(),
}).refine((b) => b.day <= DAYS_IN[b.month - 1], { message: 'That date doesn’t exist', path: ['day'] })
    .refine((b) => !b.year || b.month !== 2 || b.day !== 29 || isLeap(b.year), { message: '29 February only exists in leap years', path: ['year'] });
export const BirthdaySettingsInput = z.object({ remind: z.boolean().optional(), cardLeadDays: z.number().int().min(1).max(30).optional() });
export const CARD_LEAD_DAYS = 5;
/** Days after the birthday an unfinished card to-do stays before it expires as missed. */
export const CARD_GRACE_DAYS = 2;
export const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
/** The date key a birthday falls on in `year`: 29 February is kept on 28 February in other years. */
export function birthdayOn(b, year) {
    const day = b.month === 2 && b.day === 29 && !isLeap(year) ? 28 : b.day;
    return `${year}-${pad(b.month)}-${pad(day)}`;
}
/** Age turned on the occurrence in `occurrenceYear`. */
export function ageOn(occurrenceYear, year) {
    if (!year)
        return null;
    const n = occurrenceYear - year;
    return n >= 0 && n < 150 ? n : null;
}
/** "Sam turns 40" / "Sam’s birthday", with an optional suffix ("today"). */
export function birthdayLabel(b, when) {
    const w = when ? ` ${when}` : '';
    if (b.age === 0)
        return `${b.name} is born${w}`;
    return b.age !== null ? `${b.name} turns ${b.age}${w}` : `${b.name}’s birthday${w}`;
}
/** All-day calendar entries for the birthdays falling in [fromKey, toKey] (inclusive). */
export function birthdayEvents(list, fromKey, toKey, tz = CALENDAR_TZ) {
    const out = [];
    const y0 = parseDateKey(fromKey).y;
    const y1 = parseDateKey(toKey).y;
    for (const b of list) {
        for (let y = y0; y <= y1; y++) {
            const k = birthdayOn(b, y);
            if (k < fromKey || k > toKey)
                continue;
            if (b.year && y < b.year)
                continue;
            const end = addDaysKey(k, 1);
            const bd = { id: b.id, name: b.name, age: ageOn(y, b.year) };
            out.push({
                id: `bday-${b.id}-${y}`, calendarId: BIRTHDAY_CALENDAR_ID, title: birthdayLabel(bd), allDay: true, start: k, end,
                startMs: dayStartMs(k, tz), endMs: dayStartMs(end, tz), timeZone: null, location: null, description: b.note,
                colorId: null, color: BIRTHDAY_COLOR, attendees: [], organizer: null, reminders: { useDefault: false, overrides: [] },
                recurringEventId: null, status: 'confirmed', etag: '', htmlLink: null, meetUrl: null, updatedAt: b.updatedAt,
                kind: 'birthday', birthday: bd,
            });
        }
    }
    return out.sort((a, b) => a.startMs - b.startMs || a.title.localeCompare(b.title));
}
/** Birthdays in the next `days` days from `today` (inclusive), soonest first. */
export function upcomingBirthdays(list, today, days = 30, tz = CALENDAR_TZ) {
    return birthdayEvents(list, today, addDaysKey(today, days - 1), tz);
}
export const isBirthday = (e) => e.kind === 'birthday' || e.calendarId === BIRTHDAY_CALENDAR_ID;
/**
 * The birthday occurrence whose card window [date − lead, date + grace] contains `today` (this year's or
 * next year's, so a 2 January birthday is due in late December), or null.
 */
export function cardDue(b, today, lead = CARD_LEAD_DAYS) {
    const y = parseDateKey(today).y;
    for (const yy of [y - 1, y, y + 1]) {
        if (b.year && yy < b.year)
            continue;
        const date = birthdayOn(b, yy);
        const from = addDaysKey(date, -lead);
        const until = addDaysKey(date, CARD_GRACE_DAYS);
        if (today >= from && today <= until)
            return { year: yy, date, from, until };
    }
    return null;
}
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Tue 14 Oct" */
export const shortDate = (key) => {
    const { m, d } = parseDateKey(key);
    return `${WD[weekdayOf(key)]} ${d} ${MON[m - 1]}`;
};
/** "Send a card to Sam — birthday Tue 14 Oct (turns 40)" */
export function cardText(t) {
    return `Send a card to ${t.name} — birthday ${shortDate(t.date)}${t.age !== null && t.age > 0 ? ` (turns ${t.age})` : ''}`;
}
//# sourceMappingURL=calendar.js.map